import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Grid, Paper, Card, CardContent,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Chip,
  InputAdornment, CircularProgress, Button, Avatar, Divider, IconButton, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ComputerIcon from '@mui/icons-material/Computer';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/$/, '');

export default function AdminUserActivity() {
  // Navigation State
  const [selectedUserId, setSelectedUserId] = useState(null); 
  // Users Directory States
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Metrics States
  const [metrics, setMetrics] = useState({
    realtimeUsers: 0,
    dauMauRatio: '0.0',
    avgSession: '0m',
    newRegistrations: 0,
    retentionRate: '0%'
  });

  // Selected User Timeline Sub-page States
  const [userData, setUserData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  // Status Badge Helper
  const getStatusChip = (status) => {
    return status === 'Active' ? (
      <Chip label="Active" color="success" size="small" variant="soft" sx={{ fontWeight: '600' }} />
    ) : (
      <Chip label="Inactive" color="default" size="small" sx={{ fontWeight: '600' }} />
    );
  };

  // Search Handler
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };
  // =========================================================
  // 1. FETCH METRICS & USERS DIRECTORY
  // =========================================================
  useEffect(() => {
    if (selectedUserId) return;

    const fetchDirectoryData = async () => {
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        };

        const params = new URLSearchParams({
          search,
          page: (page + 1).toString(),
          limit: rowsPerPage.toString()
        }).toString();

        const [metricsRes, usersRes] = await Promise.all([
          fetch(`${API_BASE_URL}/admin/analytics-summary?days=7`, { headers }),
          fetch(`${API_BASE_URL}/admin/users-directory?${params}`, { headers })
        ]);

        if (metricsRes.ok) {
          const metricsData = await metricsRes.json();
          setMetrics(prev => ({ ...prev, ...metricsData }));
        }

        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setUsers(usersData.results || []);
          setTotalUsers(usersData.totalUsers || 0);
        }
      } catch (error) {
        console.error("Error fetching users directory:", error);
      } finally {
        setLoadingUsers(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchDirectoryData();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [search, page, rowsPerPage, selectedUserId]);
  // =========================================================
  // 2. FETCH SPECIFIC USER TIMELINE SUB-PAGE DATA
  // =========================================================
  useEffect(() => {
    if (!selectedUserId) return;

    const fetchUserTimeline = async () => {
      setLoadingTimeline(true);
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        };

        const res = await fetch(`${API_BASE_URL}/admin/users/${selectedUserId}/timeline`, { headers });
        if (res.ok) {
          const data = await res.json();
          setUserData(data.user);
          setTimeline(data.timeline || []);
        }
      } catch (error) {
        console.error("Error fetching user timeline:", error);
      } finally {
        setLoadingTimeline(false);
      }
    };

    fetchUserTimeline();
  }, [selectedUserId]);
  // =========================================================
  // 3. SSE LIVE ACTIVE USERS STREAM
  // =========================================================
  useEffect(() => {
    const token = localStorage.getItem('token');
    const liveStream = new EventSource(`${API_BASE_URL}/admin/realtime-users?token=${token}`);

    liveStream.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setMetrics(prev => ({ ...prev, realtimeUsers: data.onlineCount }));
      } catch (err) {
        console.error("Failed parsing SSE payload:", err);
      }
    };

    return () => liveStream.close();
  }, []);

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2.5, md: 3.5 }, bgcolor: 'transparent', minHeight: '100vh', width: '100%', boxSizing: 'border-box' }}>
      
      {/* HEADER SECTION */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2} mb={3}>
        <Box display="flex" alignItems="center" gap={1.5}>
          {selectedUserId && (
            <IconButton 
              onClick={() => setSelectedUserId(null)} 
              sx={{ bgcolor: '#ffffff', boxShadow: 1, '&:hover': { bgcolor: '#f1f5f9' }, p: 1 }}
              size="small"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Box>
            <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }} fontWeight="700" color="text.primary">
              {selectedUserId ? "User Activity Timeline" : "Users Activity"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {selectedUserId 
                ? `Viewing historical actions and endpoint logs for ${userData?.name || 'User'}` 
                : "Real-time analytics and user directory overview"}
            </Typography>
          </Box>
        </Box>
        
        <Chip 
          label={`${metrics.realtimeUsers} Active Users Right Now`} 
          color="success" 
          variant="outlined"
          sx={{ fontWeight: '600', px: 1, bgcolor: '#f0fdf4', borderColor: '#bbf7d0', alignSelf: { xs: 'flex-start', sm: 'auto' } }}
        />
      </Box>

      {/* VIEW A: USERS DIRECTORY (MAIN PAGE) */}
      {!selectedUserId ? (
        <>
          {/* TOP METRICS CARDS */}
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} mb={3}>
            <Grid item xs={6} sm={6} md={3}>
              <Card elevation={0} sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="500" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                    DAU / MAU Ratio
                  </Typography>
                  <Typography variant="h5" fontWeight="700" color="text.primary" sx={{ mt: 0.5, fontSize: { xs: '1.25rem', sm: '1.75rem' } }}>
                    {metrics.dauMauRatio}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Card elevation={0} sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="500" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                    Avg Session Duration
                  </Typography>
                  <Typography variant="h5" fontWeight="700" color="text.primary" sx={{ mt: 0.5, fontSize: { xs: '1.25rem', sm: '1.75rem' } }}>
                    {metrics.avgSession}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Card elevation={0} sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="500" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                    New Registrations
                  </Typography>
                  <Typography variant="h5" fontWeight="700" color="success.main" sx={{ mt: 0.5, fontSize: { xs: '1.25rem', sm: '1.75rem' } }}>
                    +{metrics.newRegistrations}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Card elevation={0} sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="500" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                    User Retention Rate
                  </Typography>
                  <Typography variant="h5" fontWeight="700" color="primary.main" sx={{ mt: 0.5, fontSize: { xs: '1.25rem', sm: '1.75rem' } }}>
                    {metrics.retentionRate}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* SEARCH BAR */}
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, mb: 2.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search user by name, email, or mobile..."
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Paper>

          {/* USERS DIRECTORY TABLE CONTAINER WITH HORIZONTAL SCROLL & RESPONSIVE CELLS */}
          <TableContainer 
            component={Paper} 
            elevation={0} 
            sx={{ 
              borderRadius: 2, 
              border: '1px solid #e2e8f0', 
              overflowX: 'auto', 
              width: '100%',
              maxWidth: '100%',
              bgcolor: '#ffffff'
            }}
          >
            <Table sx={{ minWidth: 700 }}>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: '600', color: '#475569', py: 1.5, px: 2, minWidth: 130 }}>User Name</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#475569', py: 1.5, px: 2, minWidth: 140 }}>Email / Mobile</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#475569', py: 1.5, px: 2, minWidth: 200 }}>Latest Action</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#475569', py: 1.5, px: 2, minWidth: 140 }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#475569', py: 1.5, px: 2, minWidth: 90 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: '600', color: '#475569', py: 1.5, px: 2, minWidth: 120 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingUsers ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No registered users found matching your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((row) => (
                    <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedUserId(row.id)}>
                      <TableCell sx={{ fontWeight: '600', color: '#1e293b', py: 1.5, px: 2 }}>{row.name}</TableCell>
                      <TableCell color="text.secondary" sx={{ py: 1.5, px: 2, wordBreak: 'break-word' }}>{row.emailOrMobile}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', color: '#334155', py: 1.5, px: 2, wordBreak: 'break-all', fontSize: '0.82rem' }}>{row.latestAction}</TableCell>
                      <TableCell color="text.secondary" sx={{ py: 1.5, px: 2, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{row.actionDateTime}</TableCell>
                      <TableCell sx={{ py: 1.5, px: 2 }}>{getStatusChip(row.status)}</TableCell>
                      <TableCell align="right" sx={{ py: 1.5, px: 2 }}>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={(e) => { e.stopPropagation(); setSelectedUserId(row.id); }}
                          sx={{ textTransform: 'none', borderRadius: 1.5, fontSize: '0.75rem', px: 1.5, whiteSpace: 'nowrap' }}
                        >
                          View Timeline
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalUsers}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{ borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', px: { xs: 1, sm: 2 } }}
            />
          </TableContainer>
        </>
      ) : (
        /* VIEW B: SINGLE USER TIMELINE SUB-PAGE */
        <Box>
          {loadingTimeline ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress size={36} />
            </Box>
          ) : (
            <>
              {/* USER INFORMATION CARD */}
              <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" gap={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: { xs: 48, sm: 60 }, height: { xs: 48, sm: 60 }, bgcolor: '#3b82f6', fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 'bold' }}>
                      {userData?.name ? userData.name.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="700" color="#0f172a" sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>
                        {userData?.name || 'User Profile'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {userData?.email}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" flexWrap="wrap" gap={{ xs: 2, sm: 3 }} sx={{ width: { xs: '100%', sm: 'auto' }, pt: { xs: 1, sm: 0 }, borderTop: { xs: '1px solid #f1f5f9', sm: 'none' } }}>
                    <Box textAlign={{ xs: 'left', sm: 'right' }}>
                      <Typography variant="caption" color="text.secondary" display="block">User ID</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: '600', wordBreak: 'break-all' }}>
                        {userData?.id}
                      </Typography>
                    </Box>
                    <Box textAlign={{ xs: 'left', sm: 'right' }}>
                      <Typography variant="caption" color="text.secondary" display="block">Registered On</Typography>
                      <Typography variant="body2" fontWeight="600">
                        {userData?.registeredAt ? new Date(userData.registeredAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>

              {/* TIMELINE EVENTS CHRONOLOGICAL LIST */}
              <Typography variant="h6" fontWeight="700" color="#1e293b" mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                Activity History
              </Typography>

              {timeline.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <Typography color="text.secondary">No recorded activity timeline for this user.</Typography>
                </Paper>
              ) : (
                timeline.map((group, groupIdx) => (
                  <Box key={groupIdx} mb={3.5}>
                    <Typography variant="subtitle2" sx={{ fontWeight: '700', color: '#64748b', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem' }}>
                      {group.groupLabel}
                    </Typography>

                    <Box sx={{ pl: { xs: 1.5, sm: 2 }, borderLeft: '2px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {group.events.map((event) => (
                        <Paper 
                          key={event.id} 
                          elevation={0} 
                          sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 2, border: '1px solid #e2e8f0', position: 'relative', ml: 0.5 }}
                        >
                          {/* Timeline Node Bullet */}
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              left: { xs: -21, sm: -25 }, 
                              top: 20, 
                              width: 10, 
                              height: 10, 
                              borderRadius: '50%', 
                              bgcolor: event.color || '#3b82f6', 
                              border: '2px solid #ffffff' 
                            }} 
                          />

                          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1}>
                            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                              <Typography variant="h6" fontSize="1.1rem">{event.icon || '📌'}</Typography>
                              <Box>
                                <Typography variant="subtitle1" fontWeight="700" color="#0f172a" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                                  {event.title}
                                </Typography>
                                <Chip 
                                  label={event.category} 
                                  size="small" 
                                  sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: '600', fontSize: '0.68rem', height: 20, mt: 0.25 }} 
                                />
                              </Box>
                            </Box>
                            
                            <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
                              <AccessTimeIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                              <Typography variant="caption" fontWeight="500">{event.time}</Typography>
                            </Box>
                          </Box>

                          <Typography variant="body2" color="#334155" sx={{ mt: 1.5, fontFamily: 'monospace', bgcolor: '#f8fafc', p: 1, borderRadius: 1.5, wordBreak: 'break-all', fontSize: { xs: '0.78rem', sm: '0.85rem' } }}>
                            {event.description}
                          </Typography>

                          {event.ip && (
                            <Box display="flex" alignItems="center" gap={0.5} mt={1} color="text.secondary">
                              <ComputerIcon fontSize="inherit" />
                              <Typography variant="caption">IP: {event.ip}</Typography>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                ))
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}